import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { MAX_FIELD_VALUE_LENGTH, MAX_SEARCH_TEXT_LENGTH } from './limits'
import { draftKey, normalizeRid, ownedBy } from './keys'
import {
  NEW_PRODUCT,
  type ProductBrowseIntent,
  type ProductField,
  type ProductFields,
  type ProductsIntentState,
  type SearchMode,
} from './types'

/**
 * Намерение в окнах товаров: что человек ищет в КАЖДОЙ базе и черновики
 * правки. Результатов поиска и карточек здесь нет — они в кэше RTK Query (R01).
 * Срок — `screen`: черновик живёт до перезагрузки; о несохранённом окно
 * предупреждает, а в браузерное хранилище черновик не пишется.
 */
const initialState: ProductsIntentState = { browse: {}, drafts: {} }

export const EMPTY_BROWSE: ProductBrowseIntent = { text: '', submitted: null }

interface FieldChange {
  resourceId: string
  target: string
  field: ProductField
  value: string
  /** Значения, с которых начинается черновик (карточка в 1С сейчас). Берутся только для нового черновика. */
  base: ProductFields
  baseVersion: string | null
  now: string
}

const productsSlice = createSlice({
  name: 'onec/products',
  initialState,
  reducers: {
    browseTextChanged(state, action: PayloadAction<{ resourceId: string; text: string }>) {
      const rid = normalizeRid(action.payload.resourceId)
      state.browse[rid] = { ...(state.browse[rid] ?? EMPTY_BROWSE), text: action.payload.text.slice(0, MAX_SEARCH_TEXT_LENGTH) }
    },
    browseSubmitted(state, action: PayloadAction<{ resourceId: string; mode: SearchMode; value: string }>) {
      const rid = normalizeRid(action.payload.resourceId)
      const value = action.payload.value.trim().slice(0, MAX_SEARCH_TEXT_LENGTH)
      const current = state.browse[rid] ?? EMPTY_BROWSE
      state.browse[rid] = {
        text: action.payload.mode === 'barcode' ? value : current.text,
        submitted: value === '' ? null : { mode: action.payload.mode, value },
      }
    },
    browseCleared(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.browse[normalizeRid(action.payload.resourceId)]
    },
    browseRestored(state, action: PayloadAction<{ resourceId: string; intent: ProductBrowseIntent }>) {
      state.browse[normalizeRid(action.payload.resourceId)] = action.payload.intent
    },
    draftFieldChanged(state, action: PayloadAction<FieldChange>) {
      const { resourceId, target, field, value, base, baseVersion, now } = action.payload
      const key = draftKey(resourceId, target)
      const draft = state.drafts[key] ?? {
        resourceId: normalizeRid(resourceId),
        target,
        base,
        values: {},
        baseVersion,
        checkedName: null,
        startedAt: now,
      }
      const clipped = value.slice(0, MAX_FIELD_VALUE_LENGTH)
      if (clipped === draft.base[field]) delete draft.values[field]
      else draft.values[field] = clipped
      if (Object.keys(draft.values).length === 0 && draft.checkedName === null) delete state.drafts[key]
      else state.drafts[key] = draft
    },
    /** «Взять из 1С»: своё значение поля отбросить, началом считать серверное. */
    draftFieldTakenFromServer(
      state,
      action: PayloadAction<{ resourceId: string; target: string; field: ProductField; server: string }>,
    ) {
      const key = draftKey(action.payload.resourceId, action.payload.target)
      const draft = state.drafts[key]
      if (draft === undefined) return
      draft.base[action.payload.field] = action.payload.server
      delete draft.values[action.payload.field]
      if (Object.keys(draft.values).length === 0) delete state.drafts[key]
    },
    /** «Оставить моё»: началом правки считать то, что в 1С сейчас; свои значения сохранить. */
    draftRebased(
      state,
      action: PayloadAction<{ resourceId: string; target: string; base: ProductFields; baseVersion: string | null }>,
    ) {
      const draft = state.drafts[draftKey(action.payload.resourceId, action.payload.target)]
      if (draft === undefined) return
      draft.base = action.payload.base
      draft.baseVersion = action.payload.baseVersion
    },
    draftSimilarChecked(state, action: PayloadAction<{ resourceId: string; name: string; now: string }>) {
      const key = draftKey(action.payload.resourceId, NEW_PRODUCT)
      const draft = state.drafts[key] ?? {
        resourceId: normalizeRid(action.payload.resourceId),
        target: NEW_PRODUCT,
        base: { name: '', article: '', barcode: '' },
        values: {},
        baseVersion: null,
        checkedName: null,
        startedAt: action.payload.now,
      }
      const name = action.payload.name.trim()
      draft.checkedName = name === '' ? null : name
      state.drafts[key] = draft
    },
    draftDiscarded(state, action: PayloadAction<{ resourceId: string; target: string }>) {
      delete state.drafts[draftKey(action.payload.resourceId, action.payload.target)]
    },
  },
  extraReducers: (builder) => {
    // Выход: черновики и поиск одного человека не достаются следующему (D-075).
    builder.addCase(userScopeCleared, () => initialState)
    // Ресурс вышел из работы: его РАБОТА (черновики правок) уходит, соседний rid не трогается. Поиск остаётся: это
    // вопрос к службе, а не работа, и его ответ — в том числе отказ `resource-not-granted`, из-за которого область и
    // закрыта, — окно обязано показать словами службы. Сброс поиска отписывал запрос, и вместо «Нет доступа» окно
    // показывало пустое «Введите наименование» (D-02 #619, e2e access). Повтора запроса нет: событие области на rid
    // приходит один раз, перечитанный запрос снова получает отказ и показывает его.
    builder.addCase(resourceScopeCleared, (state, action) => {
      for (const key of Object.keys(state.drafts)) {
        if (ownedBy(key, action.payload.resourceId)) delete state.drafts[key]
      }
    })
  },
})

export const {
  browseTextChanged,
  browseSubmitted,
  browseCleared,
  browseRestored,
  draftFieldChanged,
  draftFieldTakenFromServer,
  draftRebased,
  draftSimilarChecked,
  draftDiscarded,
} = productsSlice.actions
export const productsReducer = productsSlice.reducer

/** Снимок поиска для вкладки (D-072): битое — `undefined`. */
export function decodeBrowseIntent(raw: unknown): ProductBrowseIntent | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as { text?: unknown; submitted?: unknown }
  if (typeof value.text !== 'string') return undefined
  if (value.submitted === null || value.submitted === undefined) return { text: value.text.slice(0, MAX_SEARCH_TEXT_LENGTH), submitted: null }
  if (typeof value.submitted !== 'object') return undefined
  const submitted = value.submitted as { mode?: unknown; value?: unknown }
  if ((submitted.mode !== 'text' && submitted.mode !== 'barcode') || typeof submitted.value !== 'string') return undefined
  return { text: value.text.slice(0, MAX_SEARCH_TEXT_LENGTH), submitted: { mode: submitted.mode, value: submitted.value.slice(0, MAX_SEARCH_TEXT_LENGTH) } }
}
